import React, { useMemo } from "react";
import {
  TagsContainer,
  Columns,
  Count,
  CustomTag,
  CustomTagLeft,
  CustomTagRight,
  ErrorWrapper,
  EmptyWrapper,
} from "./Tags.styled";
import { Title } from "../Insight/Insight.styled";
import { Typography, Card, Tag, Icon, WidgetEmptyErrorPlaceholder } from "@ann/web-components";
import ExpandableChilds from "../ExpandableChilds/ExpandableChilds";

/**
 * Format expiration date for display
 * @param {string} dateString - ISO date string (e.g., "2027-01-27")
 * @returns {string} Formatted date (e.g., "Jan 27, 2027")
 */
const formatExpirationDate = (dateString) => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

/**
 * Renders a single tag item
 */
const TagItem = ({ tagData }) => (
  <CustomTag>
    <CustomTagLeft>
      <Tag
        color="informative"
        size="medium"
        text={tagData.tag}
      />
      <Icon name="timer" color="#2A2A2A" size="small" />
      <span>{tagData.ttlDays} days TTL</span>
    </CustomTagLeft>
    <CustomTagRight>
      <Count>{formatExpirationDate(tagData.expiresAt)}</Count>
    </CustomTagRight>
  </CustomTag>
);

/**
 * Renders a category card with its tags
 */
const CategoryCard = ({ category, tagsList }) => (
  <Card size="large">
    <Typography category="subtitle" size="medium" weight="bold">
      {category} <Count>({tagsList.length})</Count>
    </Typography>
    <ExpandableChilds renderChilds={2}>
      {tagsList.map((tagData, index) => (
        <TagItem key={`${category}-${index}`} tagData={tagData} />
      ))}
    </ExpandableChilds>
  </Card>
);

const Tags = ({
  hasData = false,
  loading = false,
  error = null,
  onRefresh,
  tags = {},
}) => {
  // Group categories into rows of 2-3 for layout
  const categoryEntries = useMemo(() => {
    return Object.entries(tags).filter(([, tagsList]) => tagsList.length > 0);
  }, [tags]);

  if (error) {
    return (
      <TagsContainer>
        <ErrorWrapper>
          <WidgetEmptyErrorPlaceholder
            icon="error"
            title="Something went wrong"
            message="We couldn't load snapshot tags. Please try again."
            buttonText="Retry"
            showButton={true}
            onClick={onRefresh}
          />
        </ErrorWrapper>
      </TagsContainer>
    );
  }

  if (!hasData && !loading) {
    return (
      <TagsContainer>
        <EmptyWrapper>
          <WidgetEmptyErrorPlaceholder
            icon="searchOff"
            title="No data to display here yet"
            message="We'll display behavioural and preference data once there's enough interaction across calls, messages, or notes."
            showButton={false}
          />
        </EmptyWrapper>
      </TagsContainer>
    );
  }

  // Split categories into rows of 2 for layout
  const rows = [];
  for (let i = 0; i < categoryEntries.length; i += 2) {
    rows.push(categoryEntries.slice(i, i + 2));
  }

  return (
    <TagsContainer>
      <Title>
        <Typography category="subtitle" size="medium" weight="bold">
          Customer Tags
        </Typography>
      </Title>

      <Typography category="body" size="medium" weight="regular">
        Static Descriptions that categorize this customer.
      </Typography>

      {rows.map((row, rowIndex) => (
        <Columns key={rowIndex}>
          {row.map(([category, tagsList]) => (
            <CategoryCard
              key={category}
              category={category}
              tagsList={tagsList}
            />
          ))}
        </Columns>
      ))}
    </TagsContainer>
  );
};

export default Tags;
